/* =============================================================================
   CORE / MENU-CONFIGURATOR — Pure-Logik des Gericht-Konfigurators (S155 / D1)
   =============================================================================
   Reine Funktionen ohne DOM/Storage — vollständig testbar:
     - optionGroupsFromMenu: Options-/Extra-Gruppen aus dem Bestand ableiten
       (distinct meta.optionGroup, Label = subcategory).
     - roleForGroup: Gruppe → Rolle (bun/patty/extras/dressing/option).
     - metaFromGroupSelection: angeklickte Gruppen + Defaults → menu_items.meta.
     - groupSelectionFromMeta: meta → Auswahl (Round-Trip beim Laden).
     - evalConstraints: Ziel-Chips vs. berechnete Tags/kcal → grün/rot (assistiv).
     - buildMenuItemPayload: Draft → Save-Payload + Pflichtfeld-Validierung.
   Mapping-Ziel: ausschließlich bestehende Spalten (meta/dietary_tags/category_id/
   subcategory_id/recipe_items) — KEIN DB-Schema-Touch.
============================================================================ */

// meta-Schlüssel, die der Konfigurator verwaltet (vor dem Neuschreiben geleert).
const MANAGED_META_KEYS = ['bunGroup', 'pattyGroup', 'extrasGroup', 'dressingGroup', 'defaultBun', 'defaultPatty', 'allowPattySwap', 'isBurger', 'isBowl'];

/** Rolle einer Options-Gruppe aus Label/Group/Kind ableiten. */
export function roleForGroup(label, group, kind) {
  const hay = (String(label || '') + ' ' + String(group || '')).toLowerCase();
  if (kind === 'extra') return 'extras';
  if (/patty/.test(hay)) return 'patty';
  if (/brötchen|brotchen|\bbun\b|brot/.test(hay)) return 'bun';
  if (/dressing/.test(hay)) return 'dressing';
  return 'option';
}

// =============================================================================
// S193 — Dish-driven Optionsregeln: das GERICHT entscheidet (meta.optionSets),
// nicht die Gruppe. Fallback = Heuristik/4-Slot für un-migrierte Gerichte.
// =============================================================================

/**
 * Rollen-Default-Regeln (= heutiges Verhalten OHNE meta.optionSets-Eintrag):
 * bun/patty/dressing = Pflicht + Einfachauswahl, extras/generic = Mehrfach optional.
 * @returns {{required:boolean, multi:boolean, min:number|null, max:number|null}}
 */
export function defaultRulesForRole(role) {
  switch (role) {
    case 'bun':
    case 'patty':
    case 'dressing':
      return { required: true, multi: false, min: null, max: null };
    case 'extras':
    default: // 'option' | 'generic' | unbekannt
      return { required: false, multi: true, min: null, max: null };
  }
}

/** meta.optionSets sicher als Array lesen. */
export function optionSetsFromMeta(dishMeta) {
  const arr = dishMeta && dishMeta.optionSets;
  return Array.isArray(arr) ? arr : [];
}

/** Zahl-Normalisierung: '' / null / NaN → null, sonst Number. */
function _num(v) {
  return (v === '' || v == null || !isFinite(Number(v))) ? null : Number(v);
}

/**
 * Regeln einer Optionssatz-Gruppe AM GERICHT: dish-first aus meta.optionSets,
 * sonst Heuristik + Rollen-Default (Backward-Compat für un-migrierte Gerichte).
 * @param {object} dishMeta   menu_items.meta des Gerichts
 * @param {string} groupKey   group_key des Satzes (= meta.optionGroup/addonGroup)
 * @param {{role?:string, label?:string}} [ctx]
 * @returns {{role,required,multi,min,max,defaultItem,source:'dish'|'heuristic'}}
 */
export function rulesForDishGroup(dishMeta, groupKey, ctx = {}) {
  const { role: roleHint, label } = ctx;
  const entry = optionSetsFromMeta(dishMeta).find(s => s && s.group === groupKey);
  if (entry) {
    const role = entry.role || roleHint || roleForGroup(label, groupKey);
    return {
      role,
      required: !!entry.required,
      multi: !!entry.multi,
      min: _num(entry.min),
      max: _num(entry.max),
      defaultItem: entry.defaultItem || null,
      source: 'dish'
    };
  }
  const role = roleHint || roleForGroup(label, groupKey);
  return { role, ...defaultRulesForRole(role), defaultItem: null, source: 'heuristic' };
}

/**
 * Toggle-Entscheidung für ein Add-on/Extra gegen die aufgelösten Regeln (pure).
 * @param {boolean} isSelected  Add-on aktuell schon gewählt?
 * @param {number}  currentCount aktuelle Anzahl gewählter Add-ons der Gruppe
 * @param {{multi?:boolean, max?:number|null}|null} rules
 * @returns {'remove'|'replace'|'add'|'block'}
 *   remove = abwählen · replace = Einfachauswahl (multi:false, ersetzt) ·
 *   add = hinzufügen (Mehrfach) · block = max erreicht (Toast, nichts tun)
 */
export function extraToggleAction(isSelected, currentCount, rules) {
  if (isSelected) return 'remove';
  if (!rules) return 'add';                 // keine Regel → Mehrfach unbegrenzt (heutiges Verhalten)
  if (!rules.multi) return 'replace';       // Einfachauswahl
  if (rules.max != null && Number(currentCount) >= Number(rules.max)) return 'block';
  return 'add';
}

/**
 * Validierung einer Auswahl gegen die aufgelösten Regeln (Pflicht/min/max).
 * Pure — für den Gast-Extras-Block (mpConfirmDraft) + Tests.
 * @returns {string|null} Fehlermeldung oder null
 */
export function selectionRuleError(count, rules, label) {
  const r = rules || {};
  const n = Number(count) || 0;
  const name = label || 'Auswahl';
  const min = r.min != null ? Number(r.min) : (r.required ? 1 : 0);
  if (n < min) {
    return min === 1 ? `Bitte mindestens 1 ${name} wählen.` : `Bitte mindestens ${min} ${name} wählen.`;
  }
  if (r.max != null && n > Number(r.max)) {
    return `Höchstens ${r.max} ${name} möglich.`;
  }
  return null;
}

/**
 * Options-/Extra-Gruppen aus dem Menü ableiten. Eine Gruppe = distinct
 * meta.optionGroup der option/extra-menu_items.
 * @returns {Array<{group,label,kind,role,options:Array<{id,name,isDefault}>}>}
 */
export function optionGroupsFromMenu(menu) {
  const byGroup = new Map();
  for (const m of (Array.isArray(menu) ? menu : [])) {
    if (m.deletedAt) continue;
    if (m.itemType !== 'option' && m.itemType !== 'extra') continue;
    // S193-Fix — Extra-Items verknüpfen über meta.addonGroup (nicht optionGroup);
    // sonst fehlt die Extras-Gruppe im Gericht-Picker (und im dish-generator-Zweig).
    const grp = m.meta && (m.meta.optionGroup || m.meta.addonGroup);
    if (!grp) continue;
    if (!byGroup.has(grp)) {
      byGroup.set(grp, { group: grp, label: m.subcategory || grp, kind: m.itemType, options: [] });
    }
    const g = byGroup.get(grp);
    if (m.subcategory && g.label === grp) g.label = m.subcategory;
    g.options.push({ id: m.id, name: m.name, isDefault: !!(m.meta && m.meta.isDefault) });
  }
  return [...byGroup.values()].map(g => ({ ...g, role: roleForGroup(g.label, g.group, g.kind) }));
}

/**
 * Angeklickte Gruppen (+ Default-Patty-Name) → menu_items.meta.
 * @param {Array<{group,role,on,defaultPatty?}>} selection
 * @param {object} [baseMeta] - bestehendes meta (nicht-verwaltete Keys bleiben)
 */
export function metaFromGroupSelection(selection, baseMeta = {}) {
  const meta = { ...(baseMeta || {}) };
  MANAGED_META_KEYS.forEach(k => { delete meta[k]; });
  for (const sel of (Array.isArray(selection) ? selection : [])) {
    if (!sel || !sel.on) continue;
    switch (sel.role) {
      case 'bun':
        meta.bunGroup = sel.group; meta.isBurger = true;
        if (sel.defaultBun) meta.defaultBun = sel.defaultBun; break; // S156c
      case 'patty':
        meta.pattyGroup = sel.group; meta.allowPattySwap = true;
        if (sel.defaultPatty) meta.defaultPatty = sel.defaultPatty; break;
      case 'extras':
        meta.extrasGroup = sel.group; break;
      case 'dressing':
        meta.dressingGroup = sel.group; meta.isBowl = true; break;
      default:
        break; // generische Gruppe — kein bekannter meta-Key (Power-User via JSON)
    }
  }
  return meta;
}

/**
 * meta → Auswahl-Zustand (Round-Trip). Markiert je Gruppe on/off + Default-Patty.
 * @returns {Array<{group,role,on,defaultPatty}>}
 */
export function groupSelectionFromMeta(meta, groups) {
  const m = meta || {};
  return (Array.isArray(groups) ? groups : []).map(g => {
    let on = false;
    if (g.role === 'bun') on = m.bunGroup === g.group;
    else if (g.role === 'patty') on = m.pattyGroup === g.group;
    else if (g.role === 'extras') on = m.extrasGroup === g.group;
    else if (g.role === 'dressing') on = m.dressingGroup === g.group;
    return {
      group: g.group, role: g.role, on,
      defaultPatty: (g.role === 'patty' ? (m.defaultPatty || null) : null),
      defaultBun: (g.role === 'bun' ? (m.defaultBun || null) : null) // S156c
    };
  });
}

const CONSTRAINT_DEFS = [
  { key: 'vegan',        label: 'Vegan',        test: c => (c.dietaryTags || []).includes('vegan') },
  { key: 'vegetarian',   label: 'Vegetarisch',  test: c => (c.dietaryTags || []).includes('vegetarian') || (c.dietaryTags || []).includes('vegan') },
  { key: 'gluten_free',  label: 'Glutenfrei',   test: c => (c.dietaryTags || []).includes('gluten_free') },
  { key: 'lactose_free', label: 'Laktosefrei',  test: c => (c.dietaryTags || []).includes('lactose_free') }
];

/**
 * Constraint-Check (assistiv, nicht blockierend). Nur aktive Ziele liefern Chips.
 * @param {object} targets - { vegan, vegetarian, gluten_free, lactose_free, maxKcal }
 * @param {object} computed - { dietaryTags:[], energyKcal:number }
 * @returns {Array<{key,label,ok}>}
 */
export function evalConstraints(targets, computed) {
  const t = targets || {};
  const c = computed || {};
  const out = [];
  for (const def of CONSTRAINT_DEFS) {
    if (t[def.key]) out.push({ key: def.key, label: def.label, ok: !!def.test(c) });
  }
  if (t.maxKcal != null && t.maxKcal !== '' && isFinite(Number(t.maxKcal))) {
    const max = Number(t.maxKcal);
    out.push({ key: 'maxKcal', label: `≤ ${max} kcal`, ok: Number(c.energyKcal || 0) <= max });
  }
  return out;
}

/**
 * Draft → Save-Payload + Validierung. Pflicht: Name + Rubrik (categoryId).
 * @returns {{valid:boolean, errors:string[], payload:object}}
 */
export function buildMenuItemPayload(draft) {
  const d = draft || {};
  const errors = [];
  const name = (d.name || '').trim();
  if (!name) errors.push('Name fehlt');
  if (!d.categoryId) errors.push('Rubrik fehlt');
  const price = Number(d.price);
  const payload = {
    name,
    category: d.category || '',            // String-Spalte wird per DB-Trigger aus category_id gespiegelt
    categoryId: d.categoryId || null,
    subcategoryId: d.subcategoryId || null,
    itemType: d.itemType || 'main',
    price: isFinite(price) ? price : 0,
    unitLabel: d.unitLabel || '',
    description: d.description || '',
    available: d.available !== false,
    meta: (d.meta && typeof d.meta === 'object') ? d.meta : {},
    dietaryTags: Array.isArray(d.dietaryTags) ? d.dietaryTags : []
  };
  if (d.id) payload.id = d.id;
  return { valid: errors.length === 0, errors, payload };
}

// =============================================================================
// S156c — Constraint filtert die MANUELLE Auswahl (Zutaten- + Options-Dropdowns)
// =============================================================================
// Pure, testbar. Spiegelt dieselbe Logik wie der Generator (dish-generator
// passesConstraints): vegan→dietary_tags⊇vegan; vegetarisch→vegetarian|vegan;
// glutenfrei→kein gluten-Allergen; laktosefrei→kein milk. Mehrere aktiv = UND.

/** Ist mindestens ein Diät-Constraint aktiv? (maxKcal zählt NICHT als Auswahl-Filter.) */
export function constraintFilterActive(targets) {
  const t = targets || {};
  return !!(t.vegan || t.vegetarian || t.gluten_free || t.lactose_free);
}

/** Erfüllt EINE Zutat alle aktiven Diät-Constraints? */
export function ingredientMeetsTargets(ingredient, targets) {
  const t = targets || {};
  const tags = (ingredient && ingredient.dietaryTags) || [];
  const al = (ingredient && ingredient.allergens) || [];
  if (t.vegan && !tags.includes('vegan')) return false;
  if (t.vegetarian && !(tags.includes('vegetarian') || tags.includes('vegan'))) return false;
  if (t.gluten_free && al.includes('gluten')) return false;
  if (t.lactose_free && al.includes('milk')) return false;
  return true;
}

/** Zutaten-Liste auf konforme filtern (ohne aktiven Constraint: unverändert). */
export function filterIngredientsByTargets(ingredients, targets) {
  const list = Array.isArray(ingredients) ? ingredients : [];
  if (!constraintFilterActive(targets)) return list.slice();
  return list.filter(i => ingredientMeetsTargets(i, targets));
}

/** Erfüllt EINE Option (Bun/Patty/Dressing) die Constraints? = ALLE Rezept-Zutaten konform. */
export function optionMeetsTargets(option, recipesByItem, ingredientsById, targets) {
  if (!option) return false;
  const rows = (recipesByItem && recipesByItem[option.id]) || [];
  if (!rows.length) return false; // ohne Rezept nicht bewertbar → bei Constraint nicht anbieten
  return rows.every(r => {
    const ing = ingredientsById && ingredientsById[r.ingredientId];
    return ing && ingredientMeetsTargets(ing, targets);
  });
}

/** Options-Liste auf konforme filtern (ohne aktiven Constraint: unverändert). */
export function filterOptionsByTargets(options, recipesByItem, ingredientsById, targets) {
  const list = Array.isArray(options) ? options : [];
  if (!constraintFilterActive(targets)) return list.slice();
  return list.filter(o => optionMeetsTargets(o, recipesByItem, ingredientsById, targets));
}
